/**
 * Pure normalisation helpers for GameContext.
 *
 * Extracted from `index.tsx` (PR-V3.E) to isolate the format conversion
 * concern (`gameData.start` raw -> Unix seconds) from sync and timer logic.
 *
 * Behaviour pinned by `src/__tests__/context/gameContext.test.tsx` and
 * `src/__tests__/context/normalize.test.ts`.
 */

import parseISO from "date-fns/parseISO"
import getUnixTime from "date-fns/getUnixTime"

/**
 * Raw `start` value as it can be received from Supabase: either an ISO string,
 * a wrapper object `{ isoString }`, `null`, or `undefined`.
 */
export type RawStart = string | { isoString: string } | null | undefined

/**
 * Normalise a raw `start` value into Unix seconds.
 *
 * Returns `null` when the value cannot be normalised (missing or unknown
 * shape). Callers should preserve the previous value in that case (the
 * Provider keeps `start` at its current state, which defaults to 0).
 */
export const normalizeStart = (raw: RawStart): number | null => {
  if (!raw) {
    return null
  }
  if (typeof raw === "string") {
    return getUnixTime(parseISO(raw))
  }
  if (typeof raw === "object" && typeof raw.isoString === "string") {
    return getUnixTime(parseISO(raw.isoString))
  }
  // Unknown shape: caller decides what to do. We log here to mirror the
  // historical warning emitted by the Provider before the split.
  // eslint-disable-next-line no-console
  console.warn("[GameContext] Unknown start format:", raw)
  return null
}
